import { LDFlagSet } from 'launchdarkly-js-sdk-common';
import { get, partition } from 'lodash';

import { EXPENSE_TYPE, EXPENSES_ALL, FIELD_PATHS, FREQUENCY } from '@funnels/constants';
import { Expense, Expenses } from '@funnels/types';

import { INITIAL_EXPENSES } from './initialFormValues';

export default function groupExpensesByMaritalStatus(
  expenses: Expenses[],
  marriedApplicantIds: string[],
  featureFlags: LDFlagSet
): Expenses[] {
  // no married applicants
  if (marriedApplicantIds.length < 2) {
    return expenses;
  }

  const [marriedExpenses, singleExpenses] = partition(expenses, (expenses) => {
    const applicantId = get({ expenses: [expenses] }, FIELD_PATHS.EXPENSES_APPLICANT_ID(0), '');
    return marriedApplicantIds.includes(applicantId);
  });

  const groupedExpenses = [];
  // no married expenses
  if (marriedExpenses.length === 0) {
    return expenses;
  }

  while (marriedApplicantIds.length > 0) {
    const [currentMarriedId1, currentMarriedId2] = marriedApplicantIds.splice(0, 2);
    const currentMarriedExpenses1 = marriedExpenses.find(
      (expenses: Expenses) => get({ expenses: [expenses] }, FIELD_PATHS.EXPENSES_APPLICANT_ID(0)) === currentMarriedId1
    );
    const currentMarriedExpenses2 = marriedExpenses.find(
      (expenses: Expenses) => get({ expenses: [expenses] }, FIELD_PATHS.EXPENSES_APPLICANT_ID(0)) === currentMarriedId2
    ) || { ...INITIAL_EXPENSES(), applicantId: currentMarriedId2 };

    const currentMarriedExpenses1Expense = get(
      { expenses: [currentMarriedExpenses1] },
      FIELD_PATHS.EXPENSES_EXPENSES(0),
      []
    );
    const currentMarriedExpenses2Expense = get(
      { expenses: [currentMarriedExpenses2] },
      FIELD_PATHS.EXPENSES_EXPENSES(0),
      []
    );

    const mergedExpenses = EXPENSES_ALL(featureFlags).reduce(
      (mergedExpenseList: Expense[], expenseType: EXPENSE_TYPE) => {
        const currentMarriedExpenses1ExpenseItem: Expense | undefined = currentMarriedExpenses1Expense.find(
          (expense: Expense) =>
            get({ expenses: [{ expenses: [expense] }] }, FIELD_PATHS.EXPENSES_EXPENSE_TYPE(0, 0)) === expenseType
        );

        const currentMarriedExpenses2ExpenseItem: Expense | undefined = currentMarriedExpenses2Expense.find(
          (expense: Expense) =>
            get({ expenses: [{ expenses: [expense] }] }, FIELD_PATHS.EXPENSES_EXPENSE_TYPE(0, 0)) === expenseType
        );

        const amount1 = get(
          { expenses: [{ expenses: [currentMarriedExpenses1ExpenseItem] }] },
          FIELD_PATHS.EXPENSES_EXPENSE_AMOUNT(0, 0)
        );

        const amount2 = get(
          { expenses: [{ expenses: [currentMarriedExpenses2ExpenseItem] }] },
          FIELD_PATHS.EXPENSES_EXPENSE_AMOUNT(0, 0)
        );

        const sumAmount = isFinite(amount1 + amount2) ? amount1 + amount2 : amount1 ?? amount2;

        const comments = get(
          { expenses: [{ expenses: [currentMarriedExpenses1ExpenseItem] }] },
          FIELD_PATHS.EXPENSES_EXPENSE_COMMENTS(0, 0)
        );

        mergedExpenseList.push({
          amount: sumAmount,
          frequency: FREQUENCY.MONTHLY,
          type: expenseType,
          comments,
        } as Expense);

        return mergedExpenseList;
      },
      []
    );

    groupedExpenses.push({
      ...currentMarriedExpenses1,
      groupedApplicantId: currentMarriedExpenses2?.applicantId,
      expenses: mergedExpenses,
    });
  }

  return [...groupedExpenses, ...singleExpenses] as Expenses[];
}
